/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { ModalState } from '../../../redux/modal/reducers';

import Localizer from '../../../helpers/localization';
import ButtonFX from '../../buttons/ButtonFX';
import { WidthKind } from '../../buttons/types';
import Modal, { ModalView } from '../modal';
import './PresenceCheckModal.css';

// Modal auto-close after 60 seconds
const COUNTDOWN_START = 60;

const COUNTDOWN_INTERVAL = 1_000;

export type PRESENCE_CHECK_DATA_MODAL_TYPE = {
  isLive: boolean,
};

export enum PresenceCheckModalResult {
  Resume,
  Restart,
  Exit,
}

const PresenceCheckModal = ({ modalId, presenceCheckData }: ModalState): React.Node => {
  const modalRef = useRef<ModalView | null>(null);
  const timerRef = useRef<IntervalID | null>(null);

  const [countdown, setCountdown] = React.useState<number>(COUNTDOWN_START);

  const close = useCallback((result: PresenceCheckModalResult) => {
    modalRef.current?.close(result);
  }, []);

  const updateCountdown = useCallback(() => {
    if (countdown === 0) {
      // Countdown finished: close modal
      close(PresenceCheckModalResult.Exit);
    } else {
      setCountdown(countdown - 1);
    }
  }, [close, countdown]);

  useEffect(() => {
    timerRef.current = setInterval(updateCountdown, COUNTDOWN_INTERVAL);

    return () => {
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
      }
    };
  }, [updateCountdown]);

  const setModalRef = useCallback((instance: ModalView | null) => {
    modalRef.current = instance;
  }, []);

  const handleButtonOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>, result: PresenceCheckModalResult) => {
      close(result);
    },
    [close],
  );

  return (
    <Modal
      ariaLabel={Localizer.localize('accessibility.modal.presence_checker')}
      className='presenceCheck'
      header={Localizer.localize('modal.presence_check.title')}
      innerRef={setModalRef}
      isAlert
      modalId={modalId}
    >
      <div className='question'>
        <div>{Localizer.localize('modal.presence_check.text_main')}</div>
        <div className='subText'>{Localizer.localize('modal.presence_check.text_sub')}</div>
        <div className='countdown'>{countdown}</div>
      </div>
      <div className='buttons'>
        <ButtonFX autoFocus data={PresenceCheckModalResult.Resume} onClick={handleButtonOnClick} widthKind={WidthKind.Stretched}>
          {Localizer.localize('modal.presence_check.button_resume')}
        </ButtonFX>
        {!presenceCheckData || !presenceCheckData.isLive ? (
          <ButtonFX data={PresenceCheckModalResult.Restart} onClick={handleButtonOnClick} widthKind={WidthKind.Stretched}>
            {Localizer.localize('modal.presence_check.button_restart')}
          </ButtonFX>
        ) : null}
        <ButtonFX data={PresenceCheckModalResult.Exit} onClick={handleButtonOnClick} widthKind={WidthKind.Stretched}>
          {Localizer.localize('modal.presence_check.button_exit')}
        </ButtonFX>
      </div>
    </Modal>
  );
};

export default PresenceCheckModal;
